import { Card, Stack, useTheme } from '@mui/material';

import { Strings } from '@app/constants';
import { LeavesTakenCard, TotalLeavesChart } from '@app/components/card';

interface IPeriods {
  date: string;
  period: string;
}

interface ILeave {
  leaveType: string;
  update: string;
  periods: IPeriods[];
  from: string;
}

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const EmployeeLeavesStatus = ({ data }: any) => {
  const theme = useTheme();
  const valueFormatter = (value: number) => `${value} days`;

  const chartSetting = {
    yAxis: [
      {
        label: 'Days',
      },
    ],
    series: [
      {
        dataKey: 'leavesTaken',
        label: 'Allowed',
        color: theme.palette.quaternary.royalBlue,
        valueFormatter,
        stack: 'A',
      },
      {
        dataKey: 'extraLeaves',
        label: 'Extra',
        color: theme.palette.taskboard.lightRed,
        valueFormatter,
        stack: 'A',
      },
    ],
    height: 300,
    barWidth: 20,
    barGap: 0,
  };

  const approvedLeaves = data?.leaveManagemaent?.map((leave: any) => {
    const leaveType = leave.leaveType;
    const allowedDays = Number(leave.allowedDays);
    const taken = data?.leaves?.reduce((acc: number, currentLeave: ILeave) => {
      if (currentLeave.leaveType === leaveType && currentLeave.update === 'Approved') {
        acc += currentLeave.periods.reduce((periodAcc, period) => {
          return periodAcc + (period.period === 'Full Day' ? 1 : 0.5);
        }, 0);
      }
      return acc;
    }, 0);
    const remaining = allowedDays - taken;
    return { leaveType, value: allowedDays, taken, remaining };
  });

  const leavesByMonth = months.map((month) => ({
    month,
    leavesTaken: 0,
    extraLeaves: 0,
  }));

  const leaveCountPerMonth: Record<number, number> = {};

  data?.leaves?.forEach((leave: ILeave) => {
    leave.periods?.forEach((period: IPeriods) => {
      const [day, month, year] = period?.date?.split('/') ?? [];
      if (!day || !month || !year) return;

      const dateObj = new Date(`${year}-${month}-${day}`);
      const monthIndex = dateObj.getMonth();
      const dayValue = period.period === 'Full Day' ? 1 : 0.5;

      if (!leaveCountPerMonth[monthIndex]) {
        leaveCountPerMonth[monthIndex] = 0;
      }

      const monthData = leavesByMonth[monthIndex];
      const maxAllowed = 1.5;
      const alreadyTaken = leaveCountPerMonth[monthIndex];
      const availableSlot = Math.max(0, maxAllowed - alreadyTaken);
      const toAssign = Math.min(dayValue, availableSlot);
      monthData.leavesTaken += toAssign;
      leaveCountPerMonth[monthIndex] += toAssign;

      if (dayValue > toAssign) {
        monthData.extraLeaves += dayValue - toAssign;
      }
    });
  });

  return (
    <Stack flexDirection={{ sm: 'column', md: 'row' }} mb={{ xs: 1.5, sm: 1.5, md: 3 }}>
      <Card
        sx={{
          width: { sm: '97%', md: '48%' },
          height: '380px',
          margin: '0 2% 0 2%',
          marginTop: { xs: '2%', sm: '2%', md: '0%' },
          borderRadius: '5px',
        }}
      >
        <TotalLeavesChart
          title={Strings.employeeDashborad.totalLeaves}
          leaveData={approvedLeaves}
          allowedDays={data?.allowedDays}
        />
      </Card>
      <Card
        sx={{
          width: { sm: '97%', md: '48%' },
          height: '380px',
          margin: { xs: '0 2% 0 2%', sm: '0 2% 0 2%', md: '0 2% 0 0%' },
          marginTop: { xs: '2%', sm: '2%', md: '0%' },
          borderRadius: '5px',
          overflowX: 'auto',
        }}
      >
        <LeavesTakenCard
          dataSet={leavesByMonth}
          title={Strings.employeeDashborad.leavesTaken}
          chartSetting={chartSetting}
        />
      </Card>
    </Stack>
  );
};

export { EmployeeLeavesStatus };
